import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { ToastService } from '@app/shared/service/toast.service';
import { environment } from '@env/environment';
import { CardTypePipe } from '@upr-web-primer/card-types';
import { DalService } from '@upr-web-primer/dal';
import { MsGuestsService } from '@upr-web-primer/ms-guests';
import { NotificationService } from '@upr-web-primer/notification';
import { SpinnerService } from '@upr-web-primer/spinner';
import { LocalStorageService } from '@upr-web-primer/storage';
import { get } from '@upr-web-primer/udx-utils';
import { CookieService } from 'ngx-cookie-service';
import { CreditCardTypes } from '../../account/order-history/creditcardtypes';

@Component({
  selector: 'app-edit-payment-info',
  templateUrl: './edit-payment-info.component.html',
  styleUrls: ['./edit-payment-info.component.scss'],
  standalone: false,
})
export class EditPaymentInfoComponent implements OnInit {
  editPaymentInfoForm: FormGroup;
  yearOptions: any[] = [];
  monthOptions: any[] = [];
  pageLabels: any;
  cardType: any;
  cardNumber: any;
  errorCardNumber = '';
  errorYear = '';
  errorMonth = '';
  errorSecurityCode = '';
  setCardType = '';
  existingCardTypes = CreditCardTypes;
  selectedMonth: any;
  selectedYear: any;
  securityCode: any;
  cardObject: any;
  enableSave = false;
  primaryVal = false;
  primaryValueChanged = false;
  userPaymentCardData: any;
  cardRefId: string;
  selectedAddress: any;

  constructor(
    public commonUtilityService: CommonUtilityService,
    @Inject('AppRoutes') public appRoutes: any,
    @Inject('GlobalDefaults') public globalDefault: any,
    @Inject('RelativeUrls') private readonly relativeUrls: any,
    @Inject('AppDefaults') public appDefault: any,
    @Inject('CardTypes') public cardTypes: any,
    @Inject('MiscPattern') public miscPattern: any,
    @Inject('DefaultValues') public defaultValues: any,
    public cookieService: CookieService,
    public guestsService: MsGuestsService,
    public notification: NotificationService,
    public cdr: ChangeDetectorRef,
    public toastService: ToastService,
    @Inject('sourceKeys') public sourceKeys: any,
    private readonly formBuilder: FormBuilder,
    public cardTypePipe: CardTypePipe,
    public httpClient: HttpClient,
    public dal: DalService,
    private readonly localStorage: LocalStorageService,
    private readonly router: ActivatedRoute,
    private readonly spinner: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.router?.params?.subscribe((params: Params) => {
      const cardRefId = params.cardRefId;
      this.loadCurrentCardData(cardRefId);
    });

    this.pageLabels = this.commonUtilityService?.getResolvedPageData();
    this.createForm();
    this.enableButton();
    this.generateYearOptions();
    this.generateMonthOptions();
    this.cdr.markForCheck();
  }

  loadCurrentCardData(cardRefId: string): void {
    const guestId = this.guestsService.helper.getGuestId();
    const url = this.getRelativeUrls(
      this.relativeUrls.UPDATE_PAYMENT_INFO,
      guestId,
      cardRefId,
    );
    const headers = new HttpHeaders()
      .append('guest-id', guestId)
      .append(
        'x-uniwebservice-apikey',
        this.defaultValues.UNIWebService_xApiKey,
      )
      .append('payment-card-id', cardRefId);
    this.dal
      .httpRequest(url, this.appDefault.GET, guestId, headers)
      .then((response) => {
        this.userPaymentCardData = response;
        this.userPaymentCardData.expiration.month =
          this.userPaymentCardData.expiration.month > 9
            ? `${this.userPaymentCardData.expiration.month}`
            : `0${this.userPaymentCardData.expiration.month}`;
        this.userPaymentCardData.expiration.year =
          this.userPaymentCardData.expiration.year.toString();
        this.cardObject = {
          cardno: this.userPaymentCardData.last_four_digits,
          expiryMonth: this.userPaymentCardData.expiration.month,
          expiryYear: this.userPaymentCardData.expiration.year,
          securitycode: '',
        };
        this.setFormValues();
        this.cdr.markForCheck();
      })
      .catch((error) => {
        if (error.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.setUser();
          localStorage.clear();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', environment.cookieSubDomain);
          const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
          this.commonUtilityService.redirectToOidcAppEndSession(redirectUrl);
        } else {
          this.notification?.show(error?.error?.message);
          this.commonUtilityService.eventDDlTrigger(error?.error?.message);
        }
      });
  }

  /**
   * bacToAccInfo-It will go back to My_Profile Page
   */

  bacToAccInfo(): void {
    this.commonUtilityService?.changeRoute(this.appRoutes.PAYMENT_INFO);
  }
  /**
   * onPrimaryClick-It will set the primary Address
   */

  onPrimaryClick(event: any): void {
    if (event?.target?.id) {
      if (event?.target?.id === 'primary') {
        const currentValue = event?.detail === 'checked' ? true : false;
        this.editPaymentInfoForm?.patchValue({
          primary: currentValue,
        });
      }
    }
  }
  /**
   * createForm - it will create the form with input fields
   */

  createForm(): any {
    this.editPaymentInfoForm = this.formBuilder.group({
      creditCardNumber: new FormControl(''),
      primary: new FormControl(),
    });
  }

  /**
   * setFormValues - set and patch the form values
   */

  setFormValues(): void {
    if (this.userPaymentCardData?.is_primary == true) {
      this.primaryVal = true;
    } else {
      this.primaryVal = false;
    }
    this.editPaymentInfoForm.patchValue({
      creditCardNumber: `************${this.userPaymentCardData.last_four_digits}`,
      primary: this.primaryVal,
    });
  }
  /**
   * enableButton-It will use when user changes previous value
   */

  enableButton(): any {
    this.editPaymentInfoForm.valueChanges.subscribe((currentValue) => {
      if (currentValue?.primary === this.primaryVal) {
        this.primaryValueChanged = false;
      } else {
        this.primaryValueChanged = true;
        this.validateContinueEnabler();
      }
    });
  }

  onSubmit(): void {
    this.spinner.show(this.pageLabels?.['paymentInfo.addSnack']);
    this.spinner.show();
    const guestId = this.guestsService.helper.getGuestId();
    const paymentCardId = this.userPaymentCardData?.id;
    const url = this.getRelativeUrls(
      this.relativeUrls.UPDATE_PAYMENT_INFO,
      guestId,
      paymentCardId,
    );
    const requestBody = {
      billing_address: {
        address_id: this.selectedAddress?.address_id,
        address_line1: this.selectedAddress?.address_line1,
        address_line2: this.selectedAddress?.address_line2,
        city: this.selectedAddress?.city,
        state: this.selectedAddress?.state,
        postal_code: this.selectedAddress?.postal_code,
        country_code: this.selectedAddress?.country_code,
      },
      expiration: {
        month: +this.cardObject?.expiryMonth,
        year: +this.cardObject?.expiryYear,
      },
      is_primary: get(this.editPaymentInfoForm, 'value.primary', ''),
      first_name_on_card: this.selectedAddress?.first_name,
      last_name_on_card: this.selectedAddress?.last_name,
      number: '',
      cvv: this.cardObject?.securitycode,
    };

    const xSourceId = this.commonUtilityService?.getSourceId(
      this.sourceKeys?.WEB_USH_CARD,
    );
    const eTag = this.localStorage.getItem('WALLET-ETAG');
    const headers = new HttpHeaders()
      .append('if-match', eTag)
      .append('x-source-id', xSourceId)
      .append(
        'x-uniwebservice-apikey',
        this.defaultValues.UNIWebService_xApiKey,
      )
      .append('guest-id', guestId)
      .append('x-resort-area-code', this.appDefault.RESORT_AREA_CODE)
      .append('payment-card-id', this.userPaymentCardData?.id);

    this.dal
      .httpRequest(url, this.appDefault.PUT, requestBody, headers)
      .then(() => {
        this.toastService.show({
          text: this.pageLabels?.['paymentInfo.addSnack'],
          type: this.globalDefault?.SUCCESS,
        });
        this.spinner.hide(this.pageLabels?.['paymentInfo.addSnack']);
        this.commonUtilityService?.changeRoute(this.appRoutes.PAYMENT_INFO);
      })
      .catch((error) => {
        this.spinner.hide(this.pageLabels?.['paymentInfo.addSnack']);
        this.commonUtilityService?.changeRoute(this.appRoutes.PAYMENT_INFO);
        if (error.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.setUser();
          localStorage.clear();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', environment.cookieSubDomain);
          const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
          this.commonUtilityService.redirectToOidcAppEndSession(redirectUrl);
        } else {
          this.notification?.show(error?.error?.message);
          this.commonUtilityService.eventDDlTrigger(error?.error?.message);
        }
      });
  }

  deleteCard(): void {
    this.spinner.show();
    const guestId = this.guestsService.helper.getGuestId();
    const paymentCardId = this.userPaymentCardData?.id;
    const url = this.getRelativeUrls(
      this.relativeUrls.UPDATE_PAYMENT_INFO,
      guestId,
      paymentCardId,
    );
    const xSourceId = this.commonUtilityService?.getSourceId(
      this.sourceKeys?.WEB_USH_CARD,
    );
    const eTag = this.localStorage.getItem('WALLET-ETAG');
    const headers = new HttpHeaders()
      .append('If-Match', eTag)
      .append('x-source-id', xSourceId)
      .append('guest-id', guestId)
      .append('payment-card-id', paymentCardId);
    this.dal
      .httpRequest(url, this.appDefault.DELETE, {}, headers)
      .then(() => {
        this.toastService.show({
          text: this.pageLabels?.['paymentInfo.addSnack'],
          type: this.globalDefault?.SUCCESS,
        });
        this.commonUtilityService?.changeRoute(this.appRoutes.PAYMENT_INFO);
      })
      .catch((error) => {
        this.spinner.hide();
        this.commonUtilityService?.changeRoute(this.appRoutes.PAYMENT_INFO);
        if (error.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.setUser();
          localStorage.clear();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', environment.cookieSubDomain);
          const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
          this.commonUtilityService.redirectToOidcAppEndSession(redirectUrl);
        } else if (error.status == this.globalDefault.ERROR_400) {
          this.notification?.show(error?.error?.detail);
          this.commonUtilityService.eventDDlTrigger(error?.error?.detail);
        } else {
          this.notification?.show(error?.error?.message);
          this.commonUtilityService.eventDDlTrigger(error?.error?.message);
        }
      });
  }

  public getRelativeUrls(
    url: string,
    guestId: string,
    paymentCardId: string,
  ): string {
    url = url.replace('{guest_id}', guestId);
    return url.replace('{payment_card_id}', paymentCardId);
  }

  onCreditCardValueChange(event: any): void {
    if (event?.detail?.cardNumber) {
      this.cardNumber = event?.detail?.cardNumber;
      this.cardType = this.userPaymentCardData?.issuer_code;
      if (!this.cardType) {
        this.setCardType = '';
      } else {
        this.existingCardTypes.forEach((type) => {
          if (this.cardType === type[0]) {
            this.setCardType = type[1];
            this.errorCardNumber = '';
          }
        });
      }
    }
    if (event?.detail?.month) {
      this.selectedMonth = event?.detail?.month;
    }
    if (event?.detail?.year) {
      this.selectedYear = event?.detail?.year;
    }
    this.securityCode = event?.detail?.securityCode;

    const decimalPattern = this.miscPattern.DECIMAL;

    if (this.securityCode) {
      if (
        this.cardType === this.cardTypes.AX &&
        this.securityCode.length !== 4
      ) {
        this.errorSecurityCode =
          this.pageLabels?.['paymentInfo.error.requireFourDigits'];
      } else if (
        this.cardType !== this.cardTypes.AX &&
        this.securityCode.length !== 3
      ) {
        this.errorSecurityCode =
          this.pageLabels?.['paymentInfo.error.requireThreeDigits'];
      } else if (
        this.securityCode % 1 !== 0 ||
        decimalPattern.test(this.securityCode)
      ) {
        this.errorSecurityCode =
          this.pageLabels?.['paymentInfo.error.invalidSecurityCode'];
      } else {
        this.errorSecurityCode = '';
      }
    }
    this.cardObject = {
      cardno: this.cardNumber,
      expiryMonth: this.selectedMonth,
      expiryYear: this.selectedYear,
      securitycode: this.securityCode,
    };
    this.validateMonthAndYear();
    this.validateContinueEnabler();
  }

  validateMonthAndYear(): void {
    const dateObj = new Date();
    const currentMonth = dateObj.getMonth() + 1;
    const currentYear = dateObj.getFullYear();

    if (currentYear > this.selectedYear) {
      this.errorYear = this.pageLabels?.['paymentInfo.error.invalidYear'];
    } else if (
      currentYear === parseInt(this.selectedYear) &&
      currentMonth > parseInt(this.selectedMonth)
    ) {
      this.errorMonth = this.pageLabels?.['paymentInfo.error.invalidMonth'];
      this.errorYear = '';
    } else {
      this.errorMonth = '';
      this.errorYear = '';
    }
  }

  validateContinueEnabler(): void {
    const errors =
      this.errorCardNumber ||
      this.errorMonth ||
      this.errorYear ||
      this.errorSecurityCode;
    const containsData =
      this.selectedMonth && this.selectedYear && this.securityCode;
    if (containsData && !errors) {
      this.enableSave = true;
      if (this.selectedMonth) {
        this.selectedMonth = parseInt(this.selectedMonth);
      }
      if (this.selectedMonth < 10) {
        this.selectedMonth = '0' + this.selectedMonth;
      }
    } else {
      this.enableSave = false;
    }
  }

  generateYearOptions(): void {
    const currentYear = new Date().getFullYear();
    let total = 15,
      i = 0;
    this.yearOptions = [];
    while (total > 0) {
      const year = currentYear + i;
      this.yearOptions.push({
        label: year.toString(),
        value: year.toString(),
      });
      i += 1;
      total -= 1;
    }
  }

  generateMonthOptions(): void {
    let startMonth = 1;
    let total = 12;
    while (total > 0) {
      if (total > 3) {
        this.monthOptions.push({
          label: `0${startMonth}`,
          value: `0${startMonth}`,
        });
      } else {
        this.monthOptions.push({
          label: `${startMonth}`,
          value: `${startMonth}`,
        });
      }
      startMonth += 1;
      total -= 1;
    }
  }

  setAddress(data: any): void {
    this.selectedAddress = data;
  }
}
